import {
  buildOverviewRowsForLayerSamples,
  preferredPointLabelForLayerSamples,
  preferredOverviewRow,
} from "./map-overview-facts.js";
import { mapText } from "./map-i18n.js";

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeInteger(value) {
  const number = Number.parseInt(value, 10);
  return Number.isInteger(number) ? number : null;
}

function normalizeFiniteCoordinate(value) {
  if (
    value == null ||
    typeof value === "boolean" ||
    (typeof value === "string" && !value.trim())
  ) {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function normalizeCoordinate(value) {
  const number = normalizeFiniteCoordinate(value);
  return number == null ? null : Math.round(number);
}

function normalizeWorldHeight(value) {
  return normalizeFiniteCoordinate(value);
}

export function bookmarkCoordinatesFromSelection(selection) {
  const worldX = normalizeCoordinate(selection?.worldX);
  const worldY = normalizeWorldHeight(selection?.worldY);
  const worldZ = normalizeCoordinate(selection?.worldZ);
  if (worldX == null || worldZ == null) {
    return null;
  }
  return { worldX, worldY, worldZ };
}

function trimString(value) {
  const normalized = String(value ?? "").trim();
  return normalized || "";
}

function normalizeLayerSamples(values) {
  if (!Array.isArray(values)) {
    return [];
  }
  return values.filter((sample) => isPlainObject(sample)).map((sample) => cloneJson(sample));
}

function zoneRgbFromLayerSamples(layerSamples) {
  if (!Array.isArray(layerSamples)) {
    return null;
  }
  const zoneSample = layerSamples.find(
    (sample) => trimString(sample?.layerId) === "zone_mask" && Number.isFinite(Number(sample?.rgbU32)),
  );
  return zoneSample ? Number(zoneSample.rgbU32) : null;
}

function preferredSelectionLabel(selection, options = {}) {
  const layerSamples = Array.isArray(selection?.layerSamples) ? selection.layerSamples : [];
  const layerPointLabel = preferredPointLabelForLayerSamples(layerSamples, {
    zoneCatalog: Array.isArray(options.zoneCatalog) ? options.zoneCatalog : [],
    runtimeLayers: Array.isArray(options.runtimeLayers) ? options.runtimeLayers : [],
  });
  if (layerPointLabel) {
    return layerPointLabel;
  }
  const pointLabel = trimString(selection?.pointLabel);
  if (pointLabel && pointLabel.toLocaleLowerCase() !== "clicked point") {
    return pointLabel;
  }
  const sampleLabel = layerSamples
    .map((sample) => trimString(sample?.label || sample?.name || sample?.fieldLabel))
    .find(Boolean);
  if (sampleLabel) {
    return sampleLabel;
  }
  return "";
}

function normalizedLabelKey(value) {
  return trimString(value).toLocaleLowerCase();
}

export function bookmarkCurrentPointLabel(bookmark, stateBundle = null) {
  const runtimePointLabel = trimString(bookmark?.pointLabel);
  if (runtimePointLabel) {
    return runtimePointLabel;
  }
  return preferredPointLabelForLayerSamples(bookmark?.layerSamples, {
    zoneCatalog: stateBundle?.zoneCatalog,
    runtimeLayers: stateBundle?.state?.catalog?.layers,
  }) || "";
}

function nextBookmarkId(existingBookmarks) {
  const existingIds = new Set(
    (Array.isArray(existingBookmarks) ? existingBookmarks : [])
      .map((bookmark) => trimString(bookmark?.id))
      .filter(Boolean),
  );
  if (globalThis.crypto?.randomUUID) {
    let attempt = globalThis.crypto.randomUUID();
    while (existingIds.has(attempt)) {
      attempt = globalThis.crypto.randomUUID();
    }
    return attempt;
  }
  let counter = (Date.now() % 1_000_000_000) || 1;
  let candidate = `bookmark-${counter}`;
  while (existingIds.has(candidate)) {
    counter += 1;
    candidate = `bookmark-${counter}`;
  }
  return candidate;
}

export function normalizeBookmarks(values) {
  if (!Array.isArray(values)) {
    return [];
  }
  const next = [];
  const seen = new Set();
  for (const value of values) {
    if (!isPlainObject(value)) {
      continue;
    }
    const id = trimString(value.id);
    const worldX = normalizeCoordinate(value.worldX);
    const worldY = normalizeWorldHeight(value.worldY);
    const worldZ = normalizeCoordinate(value.worldZ);
    if (!id || worldX == null || worldY == null || worldZ == null || seen.has(id)) {
      continue;
    }
    seen.add(id);
    const bookmark = {
      id,
      worldX,
      worldY,
      worldZ,
    };
    const label = trimString(value.label);
    if (label) {
      bookmark.label = label;
    }
    const pointLabel = trimString(value.pointLabel);
    if (pointLabel) {
      bookmark.pointLabel = pointLabel;
    }
    const layerSamples = normalizeLayerSamples(value.layerSamples);
    if (layerSamples.length) {
      bookmark.layerSamples = layerSamples;
    }
    const zoneRgb = normalizeInteger(value.zoneRgb);
    if (zoneRgb != null) {
      bookmark.zoneRgb = zoneRgb;
    }
    const createdAt = trimString(value.createdAt);
    if (createdAt) {
      bookmark.createdAt = createdAt;
    }
    next.push(bookmark);
  }
  return next;
}

export function normalizeSelectedBookmarkIds(bookmarks, selectedIds) {
  const bookmarkIds = new Set(normalizeBookmarks(bookmarks).map((bookmark) => bookmark.id));
  if (!Array.isArray(selectedIds) || !bookmarkIds.size) {
    return [];
  }
  const next = [];
  const seen = new Set();
  for (const value of selectedIds) {
    const normalized = trimString(value);
    if (!normalized || seen.has(normalized) || !bookmarkIds.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    next.push(normalized);
  }
  return next;
}

export function bookmarkDisplayLabel(bookmark, fallbackIndex = 0, stateBundle = null) {
  const explicitLabel = trimString(bookmark?.label);
  if (explicitLabel) {
    return explicitLabel;
  }
  const fallbackLabel =
    bookmarkCurrentPointLabel(bookmark, stateBundle) ||
    preferredOverviewRow(bookmark?.layerSamples, {
      zoneCatalog: stateBundle?.zoneCatalog,
      runtimeLayers: stateBundle?.state?.catalog?.layers,
    })?.value ||
    preferredSelectionLabel(bookmark);
  if (fallbackLabel) {
    return fallbackLabel;
  }
  return mapText("bookmarks.fallback", { index: fallbackIndex + 1 });
}

export function bookmarkCurrentPointSubtitle(bookmark, fallbackIndex = 0, stateBundle = null) {
  const currentPointLabel = bookmarkCurrentPointLabel(bookmark, stateBundle);
  if (!currentPointLabel) {
    return "";
  }
  const displayLabel = bookmarkDisplayLabel(bookmark, fallbackIndex, stateBundle);
  return normalizedLabelKey(currentPointLabel) === normalizedLabelKey(displayLabel)
    ? ""
    : currentPointLabel;
}

export function buildBookmarkOverviewRows(bookmark, fallbackIndex = 0, stateBundle = null) {
  const displayLabel = bookmarkDisplayLabel(bookmark, fallbackIndex, stateBundle);
  const currentPointLabel = bookmarkCurrentPointSubtitle(bookmark, fallbackIndex, stateBundle);
  const overviewRows = buildOverviewRowsForLayerSamples(bookmark?.layerSamples, {
    zoneCatalog: stateBundle?.zoneCatalog,
    runtimeLayers: stateBundle?.state?.catalog?.layers,
  }).filter(
    (row) =>
      !(
        trimString(row?.key) === "zone" &&
        [displayLabel, currentPointLabel]
          .map(normalizedLabelKey)
          .filter(Boolean)
          .includes(normalizedLabelKey(row?.value))
      ),
  );
  const rows = [
    {
      icon: "bookmark",
      label: mapText("bookmarks.row_label"),
      value: displayLabel,
      hideLabel: true,
    },
  ];
  return rows.concat(overviewRows);
}

export function createBookmarkFromSelection(selection, existingBookmarks = [], options = {}) {
  const coordinates = bookmarkCoordinatesFromSelection(selection);
  if (!coordinates || coordinates.worldY == null) {
    return null;
  }
  const { worldX, worldY, worldZ } = coordinates;
  const layerSamples = normalizeLayerSamples(selection?.layerSamples);
  const label = preferredSelectionLabel(selection, options);
  return {
    id: nextBookmarkId(existingBookmarks),
    worldX,
    worldY,
    worldZ,
    ...(label ? { label } : {}),
    ...(layerSamples.length ? { layerSamples } : {}),
    ...(zoneRgbFromLayerSamples(layerSamples) != null
      ? { zoneRgb: zoneRgbFromLayerSamples(layerSamples) }
      : {}),
    createdAt: new Date().toISOString(),
  };
}

function mergeRuntimeBookmarkDetails(bookmarks, runtimeBookmarks) {
  const normalizedBookmarks = normalizeBookmarks(bookmarks);
  const identityKey = (bookmark) =>
    JSON.stringify([bookmark.id, bookmark.worldX, bookmark.worldY, bookmark.worldZ]);
  const runtimeByIdentity = new Map(
    normalizeBookmarks(runtimeBookmarks).map((bookmark) => [identityKey(bookmark), bookmark]),
  );
  let changed = false;

  const nextBookmarks = normalizedBookmarks.map((bookmark) => {
    const runtimeBookmark = runtimeByIdentity.get(identityKey(bookmark));
    if (!runtimeBookmark) {
      return bookmark;
    }
    const nextBookmark = { ...bookmark };
    if (
      Array.isArray(runtimeBookmark.layerSamples) &&
      runtimeBookmark.layerSamples.length &&
      JSON.stringify(normalizeLayerSamples(bookmark.layerSamples)) !==
        JSON.stringify(normalizeLayerSamples(runtimeBookmark.layerSamples))
    ) {
      nextBookmark.layerSamples = cloneJson(runtimeBookmark.layerSamples);
      changed = true;
    }
    if (
      trimString(bookmark.pointLabel) !== trimString(runtimeBookmark.pointLabel) &&
      trimString(runtimeBookmark.pointLabel)
    ) {
      nextBookmark.pointLabel = trimString(runtimeBookmark.pointLabel);
      changed = true;
    }
    if (
      trimString(bookmark.pointLabel) &&
      !trimString(runtimeBookmark.pointLabel)
    ) {
      delete nextBookmark.pointLabel;
      changed = true;
    }
    if (
      !Number.isInteger(normalizeInteger(bookmark.zoneRgb)) &&
      Number.isInteger(normalizeInteger(runtimeBookmark.zoneRgb))
    ) {
      nextBookmark.zoneRgb = normalizeInteger(runtimeBookmark.zoneRgb);
      changed = true;
    }
    return nextBookmark;
  });

  return changed ? nextBookmarks : normalizedBookmarks;
}

export function renameBookmark(bookmarks, bookmarkId, nextLabel) {
  const normalizedId = trimString(bookmarkId);
  const normalizedLabel = trimString(nextLabel);
  return normalizeBookmarks(bookmarks).map((bookmark) =>
    bookmark.id === normalizedId
      ? {
          ...bookmark,
          ...(normalizedLabel ? { label: normalizedLabel } : {}),
        }
      : bookmark,
  );
}

export function moveBookmarkBefore(bookmarks, movingBookmarkId, targetBookmarkId, position = "before") {
  const normalizedBookmarks = normalizeBookmarks(bookmarks);
  const sourceId = trimString(movingBookmarkId);
  const targetId = trimString(targetBookmarkId);
  const sourceIndex = normalizedBookmarks.findIndex((bookmark) => bookmark.id === sourceId);
  const targetIndex = normalizedBookmarks.findIndex((bookmark) => bookmark.id === targetId);
  if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
    return normalizedBookmarks;
  }
  const next = normalizedBookmarks.slice();
  const [moved] = next.splice(sourceIndex, 1);
  const adjustedTargetIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
  const insertIndex = position === "after" ? adjustedTargetIndex + 1 : adjustedTargetIndex;
  next.splice(insertIndex, 0, moved);
  return next;
}

export function patchTouchesBookmarkSignals(patch) {
  if (!isPlainObject(patch)) {
    return false;
  }
  return Boolean(
    patch._map_bookmarks?.entries != null ||
      patch._map_ui?.bookmarks != null ||
      patch._map_runtime?.ready != null ||
      patch._map_runtime?.ui?.bookmarks != null ||
      patch._map_runtime?.view != null ||
      patch._map_runtime?.selection != null ||
      patch._map_runtime?.catalog?.layers != null,
  );
}

export function buildBookmarkPanelStateBundle(signals) {
  const runtime = isPlainObject(signals?._map_runtime) ? signals._map_runtime : {};
  const storedBookmarks = normalizeBookmarks(signals?._map_bookmarks?.entries);
  const bookmarks = mergeRuntimeBookmarkDetails(
    storedBookmarks,
    normalizeBookmarks(runtime.ui?.bookmarks),
  );
  const bookmarkUi = isPlainObject(signals?._map_ui?.bookmarks) ? signals._map_ui.bookmarks : {};
  return {
    state: {
      ready: runtime.ready === true,
      view: cloneJson(runtime.view || {}),
      selection: cloneJson(runtime.selection || {}),
      catalog: {
        layers: Array.isArray(runtime.catalog?.layers) ? cloneJson(runtime.catalog.layers) : [],
      },
    },
    bookmarks,
    storedBookmarks,
    bookmarkUi: {
      placing: bookmarkUi.placing === true,
      selectedIds: normalizeSelectedBookmarkIds(bookmarks, bookmarkUi.selectedIds),
    },
  };
}

export function selectionBookmarkKey(selection) {
  const coordinates = bookmarkCoordinatesFromSelection(selection);
  if (!coordinates || coordinates.worldY == null) {
    return "";
  }
  const { worldX, worldY, worldZ } = coordinates;
  return JSON.stringify({
    worldX,
    worldY,
    worldZ,
    pointKind: trimString(selection?.pointKind),
    pointLabel: trimString(selection?.pointLabel),
  });
}
